import React, { useState } from "react";
import { Progress, UncontrolledTooltip } from "reactstrap";
import { useTranslation } from "react-i18next";
import TableContainer from "../../components/Common/TableContainer";
import { formatMonth, formatNumber, usagePercent } from "./usageFormat";
import "./UsageParts.css";

const barColor = (percent) => {
  if (percent > 100) return "danger";
  if (percent >= 80) return "warning";
  return "success";
};

// Overage next to the count, e.g. "超量 11,350 則", so a row stays two lines.
// Overage is billed per message, so the count says more than a percent over 100.
const Overage = ({ messageCount, quota }) => {
  const { t, i18n } = useTranslation();
  if (!quota || messageCount <= quota) return null;
  return (
    <span className="text-danger fs-12 ms-2">
      {t("usage.overageValue", {
        count: formatNumber(messageCount - quota, i18n.language),
      })}
    </span>
  );
};

// Usage bar: sent / quota. Shows "Not set" when there is no quota yet.
// large: counts in body size, for the "Sent this month" figure.
export const UsageBar = ({
  messageCount,
  quota,
  showCounts = false,
  large = false,
}) => {
  const { t, i18n } = useTranslation();
  const percent = usagePercent(messageCount, quota);

  if (percent === null) {
    return <span className="text-muted">{t("usage.notSet")}</span>;
  }

  return (
    <div style={{ minWidth: 160 }}>
      <div
        className={`d-flex justify-content-between align-items-baseline ${
          large ? "mb-2" : "mb-1 fs-12"
        }`}
      >
        <span>
          {showCounts && (
            <span className={large ? "fw-semibold fs-15" : ""}>
              {t("usage.tenant.usedOfQuota", {
                used: formatNumber(messageCount, i18n.language),
                quota: formatNumber(quota, i18n.language),
              })}
            </span>
          )}
          <Overage messageCount={messageCount} quota={quota} />
        </span>
        <span
          className={`fw-medium ms-auto ${percent > 100 ? "text-danger" : ""}`}
        >
          {t("usage.usageRateValue", { percent })}
        </span>
      </div>
      <Progress
        value={Math.min(percent, 100)}
        color={barColor(percent)}
        className="usage-progress"
        style={{ height: 6 }}
      />
    </div>
  );
};

// Usage for the tenant list: "3,000／5,000 則" and any overage, percent on
// the right, bar below.
// Only the count when there is no quota yet.
export const UsageSummary = ({ messageCount, quota }) => {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const percent = usagePercent(messageCount, quota);

  if (percent === null) {
    return t("usage.messageCountValue", {
      count: formatNumber(messageCount, lng),
    });
  }

  return (
    <div style={{ minWidth: 180 }}>
      <div className="d-flex justify-content-between text-nowrap mb-1">
        <span>
          {t("usage.usedOfQuotaShort", {
            used: formatNumber(messageCount, lng),
            quota: formatNumber(quota, lng),
          })}
          <Overage messageCount={messageCount} quota={quota} />
        </span>
        <span
          className={`fw-medium ms-2 ${percent > 100 ? "text-danger" : ""}`}
        >
          {t("usage.usageRateValue", { percent })}
        </span>
      </div>
      <Progress
        value={Math.min(percent, 100)}
        color={barColor(percent)}
        className="usage-progress"
        style={{ height: 4 }}
      />
    </div>
  );
};

let infoTipCount = 0;

// "i" icon that shows a rule or explanation on hover / focus.
export const InfoTip = ({ text }) => {
  const [id] = useState(() => `info-tip-${(infoTipCount += 1)}`);
  return (
    <>
      <i
        id={id}
        className="ri-information-line text-muted ms-1 align-middle"
        role="img"
        aria-label={text}
        tabIndex={0}
        style={{ cursor: "help" }}
        onClick={(e) => e.stopPropagation()}
      ></i>
      <UncontrolledTooltip target={id} placement="top">
        {text}
      </UncontrolledTooltip>
    </>
  );
};

// How messages are counted, shared by every usage view.
export const CountInfoTip = () => {
  const { t, i18n } = useTranslation();
  const space = i18n.language === "en" ? " " : "";
  return (
    <InfoTip
      text={`${t("usage.countHint")}${space}${t("usage.timezoneHint")}`}
    />
  );
};

export const SendingStatusBadge = ({ status }) => {
  const { t } = useTranslation();
  const paused = status === "paused";
  return (
    <span
      className={`badge ${paused ? "badge-soft-danger" : "badge-soft-success"}`}
    >
      {t(
        paused
          ? "tenants.details.sendingControl.status.paused"
          : "tenants.details.sendingControl.status.active"
      )}
    </span>
  );
};

export const planLabel = (t, plan) =>
  plan ? t(`tenants.details.plan.options.${plan}`) : t("usage.notSet");

export const quotaLabel = (t, quota, lng) =>
  quota
    ? t("usage.messageCountValue", { count: formatNumber(quota, lng) })
    : t("usage.notSet");

// Last 6 months for one tenant (newest first), usage shown the same way as
// the tenant list. Used on the tenant details
// page and the tenant's own Plan & Usage page.
export const RecentUsageTable = ({ months }) => {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;

  // The plan column only helps when the plan changed within these months.
  const planChanged = new Set(months.map((m) => m.plan)).size > 1;

  const columns = [
    {
      Header: t("usage.columns.month"),
      accessor: "month",
      Cell: ({ value }) => formatMonth(value, lng),
    },
    ...(planChanged
      ? [
          {
            Header: t("usage.columns.plan"),
            accessor: "plan",
            Cell: ({ value }) => planLabel(t, value),
          },
        ]
      : []),
    {
      Header: t("usage.columns.usage"),
      accessor: "messageCount",
      width: 260,
      disableSortBy: true,
      Cell: ({ row }) => (
        <UsageSummary
          messageCount={row.original.messageCount}
          quota={row.original.quota}
        />
      ),
    },
  ];

  if (!months.length) {
    return (
      <div className="text-center text-muted py-4">{t("usage.empty")}</div>
    );
  }

  return (
    <TableContainer
      columns={columns}
      data={months}
      loading={false}
      pageCount={1}
      hasPreviousPage={false}
      hasNextPage={false}
      pageParams={{ currentPage: 1, rowsPerPage: months.length }}
      setPageParams={() => {}}
      disableRowModal={true}
      isGlobalFilter={false}
      tableClass="custom-header-css mb-0"
      theadClass="text-muted"
    />
  );
};
